/**
 * The CLINIC's clock, not the device's.
 *
 * Every clinical timestamp is derived from the true instant, expressed in the clinic's time zone
 * (Africa/Addis_Ababa, UTC+3), with the offset written explicitly: '2026-10-10T18:20:05+03:00'.
 * A tablet whose own zone or clock settings are wrong therefore still writes the right local time,
 * and the stored value is unambiguous (FHIR `dateTime` with offset). ADHERE learned this the hard
 * way. Never use `Date.prototype.toISOString()` for a clinical date: it is UTC, and between
 * 21:00 and 24:00 UTC it names the WRONG DAY for Ethiopia.
 */
import { isoToEth } from './ethiopian.js';
export const DEFAULT_CLINIC_TZ = 'Africa/Addis_Ababa';
const pad2 = (n) => String(Math.abs(n)).padStart(2, '0');
/**
 * Create a clock pinned to a time zone. Throws if the zone is unknown, or if this runtime has no
 * time-zone data: a clinic clock that silently falls back to the device's zone is exactly the
 * failure this module exists to prevent.
 */
export function createClinicClock(timeZone = DEFAULT_CLINIC_TZ, nowFn = () => new Date()) {
    let fmt;
    try {
        fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    }
    catch {
        throw new RangeError(`Unknown time zone: ${timeZone}`);
    }
    if (fmt.resolvedOptions().timeZone.toLowerCase() !== timeZone.toLowerCase()) {
        throw new RangeError(`This runtime cannot resolve time zone ${timeZone}`);
    }
    const parts = (instant) => {
        if (Number.isNaN(instant.getTime()))
            throw new RangeError('Invalid instant');
        const o = {};
        for (const p of fmt.formatToParts(instant)) {
            if (p.type in { year: 1, month: 1, day: 1, hour: 1, minute: 1, second: 1 }) {
                o[p.type] = p.value;
            }
        }
        if (o.hour === '24')
            o.hour = '00'; // some engines render midnight as 24
        return o;
    };
    const offset = (instant, p) => {
        const wallAsUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
        const instantSec = Math.floor(instant.getTime() / 1000) * 1000;
        const mins = Math.round((wallAsUtc - instantSec) / 60000);
        return `${mins < 0 ? '-' : '+'}${pad2(Math.trunc(mins / 60))}:${pad2(mins % 60)}`;
    };
    const toClinicDate = (instant) => {
        const p = parts(instant);
        return `${p.year}-${p.month}-${p.day}`;
    };
    const toClinicDateTime = (instant) => {
        const p = parts(instant);
        return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${offset(instant, p)}`;
    };
    return {
        timeZone,
        now: () => toClinicDateTime(nowFn()),
        today: () => toClinicDate(nowFn()),
        todayEth: () => {
            const e = isoToEth(toClinicDate(nowFn()));
            if (!e)
                throw new RangeError('Clinic date is outside the supported calendar range');
            return e;
        },
        toClinicDateTime,
        toClinicDate,
    };
}
//# sourceMappingURL=clock.js.map